import { queryOptions, useQuery } from '@tanstack/react-query';

// Types
// TrackingPixel is exported from @brandclub/common-ui/analytics, imported here for type usage
import type { TrackingPixel } from '../../analytics';

export { StoreBrandingType } from '../../BrandclubLanding/type';
import type { StoreBrandingType } from '../../BrandclubLanding/type';

// IAppAuthConfig is exported from @brandclub/common-ui/Auth, imported here for type usage
import type { IAppAuthConfig } from '../../Auth';
import { getAuthorizationHeader } from '../../Auth/accessToken';
import { resolveBrandclubHostDomain } from '../../utils/domain';
import type {
  AppBootstrapV2,
  AuthTypeV2,
  ModuleSettingsPublicV2,
  StoreLocatorCapabilityPublicV2,
} from './appContextV2QueryOptions';

export interface IAppDomainConfig {
  domainName: string;
  brandId: number;
  brandName: string;
  storeBrandingType: StoreBrandingType;
  customShopDomainName?: string;
  mobileAppLink?: string;
  checkoutAllowedOfferType?: 'firstPartyOnly' | 'thirdPartyOnly' | 'both';
  pixels?: TrackingPixel[];
  abTestingTreatmentResults?: {
    [experimentName: string]: {
      treatment: string;
      config?: string;
    };
  };
  trendingSearches?: string[];
  trendingSearchesLLM?: string[];
  customShopPageUrls?: {
    popularProductsPage?: string;
    homePage?: string;
  };
  /**
   * US state codes where Meta Limited Data Use (LDU) should be applied
   * If not provided, falls back to defaults in limitedDataUse.ts
   * @example ['RI', 'KY', 'IN']
   */
  lduAffectedStates?: string[];
}

export interface BrandAppConfig {
  appConfigId: string;
  version: string;
  appName: string;
  companyId: string;
  appStatus: string;
  brandIds: number[];
  brandclubIds: string[];
  retailerIds: number[];
  mobileAppConfig?: any;
  updatedBy: string;
  updatedTime: string;
  publishedVersion: number;
  appId: string;
  appType: string;
  appStoreUrl: string;
  playStoreUrl: string;
  configId: string;
  apps: {
    appConfigId: string;
    appDescription: string;
    appId: string;
    appName: string;
    appSlug: string;
    appStatus: string;
    appType: string;
  }[];
  branchIOConfig: {
    webAppBranchLink: string;
    mobileAppBranchLink: string;
    accessToken: string;
    key: string;
    appId: string;
  };
  socialLinks?: any;
  membershipPageConfig?: {
    surveyPicture?: string;
    logoPicture?: string;
  };
  checkoutPageConfig?: {
    logoPicture?: string;
  };
  conciergeAgentName?: string;
  /**
   * Server-computed canonical concierge URL for the brand's chosen serving
   * surface (Plan 6 / D-23): `https://<host>/concierge` when the app serves
   * on its ShopperOS/custom domain, `https://<shop>/a/sos/concierge` when it
   * serves on the brand's own Shopify store via App Proxy. Cross-surface
   * links (the embed bubble's expand/history icons) must render this value
   * as-is — never re-derive it client-side. Absent until the backend
   * projects it (or when the chosen surface isn't resolvable) — fall back to
   * existing behavior.
   */
  canonicalConciergeUrl?: string;
  /**
   * Operator-chosen consumer serving surface (Plan 6 / D-23). Absent ⇒
   * `shopperos_domain`. Theme embeds must NOT invent App Proxy `/a/sos`
   * traffic unless this is `shopify_app_proxy` or `__BC_ROUTE_PATH__` is set.
   */
  servingSurface?: 'shopperos_domain' | 'shopify_app_proxy';
  /**
   * Operator-curated list of retailers whose product CTAs should
   * deep-link to the retailer's own product detail page (e.g. "Buy on
   * Amazon.com"). Driven by the V2 `where_to_buy` capability; absent =
   * disabled. Consumers (`pages` PDP/product cards, `common-ui` shared
   * product card components, mobile) read this to decide when to
   * render a deep-link CTA — URL construction stays in
   * `ProductMetaDataService.getRetailerProductPageUrl` so we don't
   * inline brand/category/title context here.
   */
  whereToBuy?: {
    retailerIds: number[];
  };
  /**
   * Operator-curated list of retailers whose physical-store finder
   * should render. Each entry is a retailer with parsed store data
   * ready to query (`parseStatus === 'ready'` on the underlying
   * `store_locator_stores` binding). Counts and `lastParsedAt` are
   * echoed so consumers can render "1,820 stores · refreshed 2 days
   * ago" without a follow-up call. `appLocations` is the app's own list.
   */
  storeLocator?: StoreLocatorCapabilityPublicV2;
  /** The module settings from the V2 bootstrap. A V1 config has none. */
  moduleSettings?: ModuleSettingsPublicV2;
}

export interface AppConfig {
  /**
   * How this app authenticates shoppers (D-42): `stackline` (Brandclub phone
   * sign-in), `shopify` (the merchant's own Shopify customer login) or
   * `no_auth` (anonymous visitors only).
   *
   * Optional only because the V1 config path has no such concept; treat a
   * missing value as "fall back to whether `authConfig` is present".
   */
  authType?: AuthTypeV2;
  /** Present only under `stackline` — it is what Amplify needs. */
  authConfig?: IAppAuthConfig;
  domainConfig: IAppDomainConfig;
}

export interface SuggestedQueryItem {
  query: string;
  imageUrl: string;
}

export interface SuggestedQueriesResponse {
  suggestedQuestions: SuggestedQueryItem[];
}

// Constants
export enum CachedQueryKeys {
  AppConfig = 'appConfig',
  AppTheme = 'appTheme',
  UserCart = 'userCart',
  BrandAppConfigs = 'brandAppConfigs',
  BrandClub = 'brandClub',
  DTCProduct = 'dtcProduct',
  SiteProduct = 'siteProduct',
  UserProfile = 'userProfile',
  ThemeSetup = 'themeSetup',
  SuggestedQueries = 'suggestedQueries',
}

export const THIRTY_MINUTES_IN_MS = 1000 * 60 * 30;
export const FIVE_MINUTES_IN_MS = 1000 * 60 * 5;

// Types for query options
export type BCQueryOptions<Fn extends (...args: any) => any, TData, TSelect = TData> = Partial<
  Omit<ReturnType<Fn>, 'select'> & { select: (data: TData) => TSelect }
>;

const getCommonRequestHeaders = () => {
  return {
    // "X-Client-AppId": ConfigUtils.getWebAppId(), // TODO: Temporarily do not send this header
  };
};

// Module-level dedupe so multiple V1 hooks adapting from V2 share one fetch.
// React Query dedupes by key, but `useAppConfig` and `useBrandAppConfig` use
// different keys and both want the same bootstrap — this collapses them.
//
// Reset on BOTH success and failure so React Query's `staleTime` and
// `invalidateQueries` stay authoritative; otherwise the first response is
// pinned for the module's lifetime and sign-in / sign-out keeps reading
// pre-auth data until a full page reload.
let _bootstrapPromise: Promise<AppBootstrapV2> | null = null;
/** The server's last answer to the bootstrap request: true for a V2 app. Null until an answer arrives. */
let isBrandAppV2Answer: boolean | null = null;
export const getBootstrapShared = async (signal?: AbortSignal, apiBaseUrl?: string): Promise<AppBootstrapV2> => {
  if (!_bootstrapPromise) {
    const DOMAIN = apiBaseUrl || resolveBrandclubHostDomain();
    const inflight = (async () => {
      const res = await fetch(DOMAIN + '/api-rewards/brandapp-v2/bootstrap', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...getCommonRequestHeaders(),
          ...(await getAuthorizationHeader()),
        },
        signal,
      });
      if (!res.ok) {
        isBrandAppV2Answer = false;
        throw new Error(`Error fetching V2 bootstrap (status ${res.status})`);
      }
      const bootstrap = (await res.json()) as AppBootstrapV2;
      isBrandAppV2Answer = true;
      return bootstrap;
    })();
    _bootstrapPromise = inflight;
    inflight.then(
      () => {
        if (_bootstrapPromise === inflight) _bootstrapPromise = null;
      },
      () => {
        if (_bootstrapPromise === inflight) _bootstrapPromise = null;
      },
    );
  }
  return _bootstrapPromise;
};

/**
 * True for a BrandApp V2 app. It reuses the server's answer to an earlier bootstrap request, so a legacy app
 * is not asked twice. A request with no answer counts as not V2.
 * An error status counts as not V2, the same as the config fallback. A network error leaves no answer, so this
 * function asks once.
 */
export const isBrandAppV2 = async (): Promise<boolean> => {
  if (isBrandAppV2Answer !== null) {
    return isBrandAppV2Answer;
  }
  try {
    await getBootstrapShared();
    return true;
  } catch {
    return false;
  }
};

export const bootstrapToAppConfig = (b: AppBootstrapV2): AppConfig => {
  const cognito = b.auth.cognito;
  // Older servers predate `auth.authType`; infer it from the Cognito block so a
  // stale backend still behaves exactly as before.
  const authType: AuthTypeV2 = b.auth.authType ?? (cognito ? 'stackline' : 'no_auth');
  // `auth.cognito` is absent by design for a guest-only app with no
  // authentication binding configured (see `toAuthPublic` on the bootstrap
  // controller) — not a malformed response. Downstream (`AppInitializer`)
  // already treats a missing `authConfig` as "skip Amplify auth init".
  return {
    authType,
    authConfig: cognito
      ? {
          userPoolId: cognito.userPoolId,
          userPoolClientId: cognito.userPoolClientId,
          identityPoolId: cognito.identityPoolId,
          signInDomain: cognito.signInDomain,
          region: cognito.region,
        }
      : undefined,
    domainConfig: {
      domainName: b.identity.hostname,
      brandId: b.identity.brand.brandId,
      brandName: b.identity.brand.brandName,
      storeBrandingType: (b.identity.brand.storeBrandingType ?? 'CustomStore') as IAppDomainConfig['storeBrandingType'],
      customShopDomainName: b.identity.brand.customShopDomainName,
      mobileAppLink: b.identity.brand.mobileAppLink,
      pixels: b.tracking.pixels as unknown as IAppDomainConfig['pixels'],
      abTestingTreatmentResults: b.tracking.abTesting,
      customShopPageUrls: b.identity.brand.customShopPageUrls,
      lduAffectedStates: b.tracking.lduAffectedStates,
    },
  };
};

export const bootstrapToBrandAppConfig = (b: AppBootstrapV2): BrandAppConfig => {
  const distribution = b.capabilities.distribution;
  const deepLink = b.capabilities.deepLinking;
  const whereToBuy = b.capabilities.whereToBuy;
  const storeLocator = b.capabilities.storeLocator;
  return {
    appConfigId: b.app.appId,
    version: 'CURRENT',
    appName: b.app.appName,
    companyId: '',
    appStatus: 'active',
    brandIds: b.app.brandIds,
    brandclubIds: b.app.brandclubIds.map(String),
    retailerIds: b.app.retailerIds,
    updatedBy: '',
    updatedTime: '',
    publishedVersion: 0,
    appId: b.app.appId,
    appType: b.app.appType,
    appStoreUrl: distribution?.appStoreUrl ?? '',
    playStoreUrl: distribution?.playStoreUrl ?? '',
    configId: b.app.appId,
    apps: [
      {
        appConfigId: b.app.appId,
        appDescription: b.app.description ?? '',
        appId: b.app.appId,
        appName: b.app.appName,
        appSlug: distribution?.appSlug ?? '',
        appStatus: 'active',
        appType: b.app.appType,
      },
    ],
    branchIOConfig: {
      webAppBranchLink: deepLink?.webAppBranchLink ?? '',
      mobileAppBranchLink: deepLink?.mobileAppBranchLink ?? '',
      accessToken: '',
      key: deepLink?.apiKey ?? '',
      appId: deepLink?.branchAppId ?? '',
    },
    whereToBuy: whereToBuy ? { retailerIds: whereToBuy.retailerIds } : undefined,
    storeLocator: storeLocator
      ? {
          retailers: storeLocator.retailers,
          ...(storeLocator.appLocations && { appLocations: storeLocator.appLocations }),
        }
      : undefined,
    canonicalConciergeUrl: b.app.canonicalConciergeUrl,
    servingSurface: b.app.servingSurface,
    moduleSettings: b.moduleSettings,
  };
};

// API functions
//
// V2-first / V1-fallback: a successful `/brandapp-v2/bootstrap` is the sole
// signal that this is a BrandApp V2 app, so we always try it first and adapt
// its projection to the V1-shaped config. A non-V2 app 404s the bootstrap
// (getBootstrapShared throws), and we fall back to the legacy V1 endpoint.
const getAppConfig = async (signal?: AbortSignal): Promise<AppConfig> => {
  try {
    return bootstrapToAppConfig(await getBootstrapShared(signal));
  } catch {
    // Not a V2 app (bootstrap 404) or bootstrap failed → legacy V1 config.
  }
  const DOMAIN = resolveBrandclubHostDomain();
  const res = await fetch(DOMAIN + '/api-rewards/config/webApp/getAppConfig', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...getCommonRequestHeaders(),
      // Note: getAuthorizationHeader would need to be imported from common-ui
      // For now, we'll assume it's available or handle auth differently
    },
    signal,
  });
  if (res.ok) {
    return res.json();
  } else {
    throw new Error('Error fetching app config');
  }
};

const getBrandAppConfigs = async (signal?: AbortSignal, apiBaseUrl?: string): Promise<BrandAppConfig> => {
  try {
    return bootstrapToBrandAppConfig(await getBootstrapShared(signal, apiBaseUrl));
  } catch {
    // Not a V2 app (bootstrap 404) or bootstrap failed → legacy V1 config.
  }
  const DOMAIN = apiBaseUrl || resolveBrandclubHostDomain();
  const res = await fetch(DOMAIN + '/api-rewards/brandapp/brandAppConfig', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...getCommonRequestHeaders(),
      ...(await getAuthorizationHeader()),
    },
    signal,
  });
  if (res.ok) {
    return res.json();
  } else {
    throw new Error('Error fetching brand app configs');
  }
};

const getSuggestedQueries = async (
  _signal?: AbortSignal, // unused — see fetch call below for rationale
  brandId?: number,
  apiBaseUrl?: string,
): Promise<SuggestedQueriesResponse> => {
  const DOMAIN = apiBaseUrl || resolveBrandclubHostDomain();

  const url = new URL(DOMAIN + '/api-rewards/concierge/suggestedQueries');
  if (brandId) {
    url.searchParams.set('brandId', brandId.toString());
  }

  try {
    const authHeaders = await getAuthorizationHeader();

    // Intentionally NOT passing the abort signal — this endpoint is slow
    // (OpenAI + DALL-E generation) but results are cached for 24h on the server.
    // Letting it complete in the background populates the cache for future visits.
    const res = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getCommonRequestHeaders(),
        ...authHeaders,
      },
    });

    if (res.ok) {
      const data = await res.json();

      return data as SuggestedQueriesResponse;
    } else {
      console.error('getSuggestedQueries - error response:', res.status, res.statusText);
      throw new Error('Error fetching suggested queries');
    }
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return { suggestedQuestions: [] };
    }
    console.error('getSuggestedQueries - caught error:', error);
    throw error;
  }
};

// Query options
export const brandAppConfigQueryOptions = (apiBaseUrl?: string) => {
  return queryOptions({
    queryKey: [CachedQueryKeys.BrandAppConfigs],
    queryFn: ({ signal }) => getBrandAppConfigs(signal, apiBaseUrl),
    staleTime: THIRTY_MINUTES_IN_MS,
  });
};

export const appConfigQueryOptions = () => {
  return queryOptions({
    queryKey: [CachedQueryKeys.AppConfig],
    queryFn: ({ signal }) => getAppConfig(signal),
    // since config data rarely changes, we can use the cached data for longer and refresh it every 30 minutes
    staleTime: THIRTY_MINUTES_IN_MS,
  });
};

export const suggestedQueriesQueryOptions = (brandId?: number, apiBaseUrl?: string) => {
  return queryOptions({
    queryKey: [CachedQueryKeys.SuggestedQueries, brandId],
    queryFn: ({ signal }) => getSuggestedQueries(signal, brandId, apiBaseUrl),
    staleTime: FIVE_MINUTES_IN_MS,
  });
};

// Hooks
export const useAppConfig = <TSelect = AppConfig>(
  args?: BCQueryOptions<typeof appConfigQueryOptions, AppConfig, TSelect>,
) => {
  return useQuery({
    ...appConfigQueryOptions(),
    ...args,
    select: args?.select,
  });
};

export const useDomainConfig = (args?: BCQueryOptions<typeof appConfigQueryOptions, AppConfig, AppConfig>) => {
  const { data: appConfig, ...rest } = useAppConfig<AppConfig>(args);
  return {
    data: appConfig?.domainConfig,
    ...rest,
  };
};

export const useBrandAppConfig = <TSelect = BrandAppConfig>(
  apiBaseUrl?: string,
  args?: BCQueryOptions<typeof brandAppConfigQueryOptions, BrandAppConfig, TSelect>,
) => {
  return useQuery({
    ...brandAppConfigQueryOptions(apiBaseUrl),
    ...args,
    select: args?.select,
  });
};

export const useSuggestedQueries = <TSelect = SuggestedQueriesResponse>(
  brandId?: number,
  apiBaseUrl?: string,
  args?: BCQueryOptions<typeof suggestedQueriesQueryOptions, SuggestedQueriesResponse, TSelect>,
) => {
  return useQuery({
    ...suggestedQueriesQueryOptions(brandId, apiBaseUrl),
    ...args,
    select: args?.select,
  });
};
